// Pricing helpers — single source of truth for displayed amounts.
// Subscription is £19/month, billed on the 1st of every month with Stripe
// proration. First charge is prorated for the remaining days of the current
// month (inclusive of today).

export const MONTHLY_PRICE_GBP = 19;
export const HALF_PROMO_PRICE_GBP = 9.5;

export function formatGBP(amount: number): string {
  if (Number.isInteger(amount)) return `£${amount}`;
  return `£${amount.toFixed(2)}`;
}

/**
 * Prorated amount for today's first charge.
 * @param monthly The full monthly price in GBP.
 * @param now Optional date (for testing).
 */
export function proratedFirstCharge(monthly = MONTHLY_PRICE_GBP, now: Date = new Date()): number {
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = now.getDate();
  const daysRemaining = daysInMonth - today + 1; // include today
  const amount = (monthly * daysRemaining) / daysInMonth;
  return Math.round(amount * 100) / 100;
}

export function firstChargeLabel(opts?: { isFullDiscount?: boolean; isHalfPromo?: boolean }): string {
  if (opts?.isFullDiscount) return "£0";
  const base = opts?.isHalfPromo ? HALF_PROMO_PRICE_GBP : MONTHLY_PRICE_GBP;
  return formatGBP(proratedFirstCharge(base));
}

export function monthlyLabel(opts?: { isFullDiscount?: boolean; isHalfPromo?: boolean }): string {
  if (opts?.isFullDiscount) return "£0";
  if (opts?.isHalfPromo) return formatGBP(HALF_PROMO_PRICE_GBP);
  return formatGBP(MONTHLY_PRICE_GBP);
}

/**
 * Human-readable pricing sentence for signup/checkout.
 * Assumes a repeating coupon that gives 50 % off for 2 months:
 * today (prorated half), 1st of next month (half), then full price.
 */
export function signupPricingSentence(opts?: { isFullDiscount?: boolean; isHalfPromo?: boolean }): string {
  if (opts?.isFullDiscount) {
    return "You'll be charged £0 today and then £0/month — cancel anytime.";
  }
  if (opts?.isHalfPromo) {
    const today = firstChargeLabel({ isHalfPromo: true });
    return `You'll be charged ${today} today (prorated), then ${monthlyLabel({ isHalfPromo: true })} on the 1st of next month, then ${monthlyLabel()}/month — cancel anytime.`;
  }
  const today = firstChargeLabel();
  return `You'll be charged ${today} today (prorated for the rest of this month) and then ${monthlyLabel()}/month on the 1st — cancel anytime.`;
}

